"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Bridge interstitial — a 5-second full-bleed beat between scenarios that
 * resets audience attention. Variants:
 *
 *   "a" → Three weeks later (T1 → T2)
 *   "b" → Same engine. Reverse direction. (T2 → T3)
 *
 * Visually a deliberate break in the dark teal palette — light cream
 * surface, dark text — so the audience feels the scene change at a
 * glance. Reads i18n strings from the existing `bridge.*` namespace.
 */
export function BridgeBeat({ variant, className }: { variant: "a" | "b"; className?: string }) {
  const t = useTranslations("bridge");
  const badge    = variant === "a" ? t("aBadge")    : t("bBadge");
  const subtag   = variant === "a" ? t("aSubtag")   : t("bSubtag");
  const headline = variant === "a" ? t("aHeadline") : t("bHeadline");
  const subcopy  = variant === "a" ? t("aSubcopy")  : t("bSubcopy");

  return (
    <div className={clsx(
      "flex h-full items-center justify-center px-12",
      "bg-gradient-to-br from-orbitra-teal-faint via-orbitra-paper to-orbitra-teal-faint",
      className,
    )}>
      <div className="flex max-w-3xl flex-col items-center text-center">
        <div className="flex items-center gap-3">
          <span className="rounded-full border border-orbitra-teal-dark/40 bg-orbitra-teal-dark/8 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-orbitra-teal-dark">
            {badge}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-dark/70">
            {subtag}
          </span>
        </div>
        <h2 className="mt-6 font-display text-4xl font-semibold tracking-tight text-orbitra-text">
          {headline}
        </h2>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-orbitra-text/75">
          {subcopy}
        </p>
        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.2em] text-orbitra-muted">
          Press <kbd className="rounded border border-orbitra-muted/40 px-1.5 py-0.5 mx-1 normal-case tracking-normal">N</kbd> to continue
        </p>
      </div>
    </div>
  );
}
